/**
 * Apollo-client resolver — the canonical seam every store slice uses
 * to dispatch GraphQL operations.
 *
 * Why a helper instead of importing `@/shared/api/apolloClient` directly:
 *
 *   1. **Setup-context awareness.** `useApollo()` (`./apollo.ts`) only
 *      resolves a client from a Vue injection context. Outside setup
 *      (e.g. a `node --test` harness importing a slice in isolation, or
 *      a module-scope `const apolloClient = resolveClient()` bind
 *      evaluated before `app.mount('#app')`), `useApollo()` throws.
 *      The resolver transparently falls back to the singleton
 *      `apolloClient` so test harnesses and the established module-
 *      scope binding pattern every store slice uses keep working
 *      without each slice re-implementing the try/catch.
 *
 *   2. **No silent-then-throw dev guard.** The fallback is intentional
 *      in BOTH dev and production — the singleton is the same client
 *      the host provides, so the runtime contract is the same
 *      regardless of where `resolveClient()` is called from. A
 *      component that bypasses the injection key still gets the
 *      host-owned client; a setup-context caller without a provider
 *      still gets the singleton (and `useApollo()`'s own error,
 *      `'ApolloPort not provided and DefaultApolloClient is missing'`,
 *      surfaces the genuine "no Apollo provide anywhere" case at the
 *      `useApollo()` site, which is the right place to flag it).
 *
 *   3. **Single import surface.** Every slice imports `resolveClient`
 *      from one place. Phase 2 of the v2 refactor moved the bulk of
 *      state-management port plumbing under `@/shared/ports/*`;
 *      `resolveClient` slots in alongside `useApollo`, `useSession`,
 *      `useGuild`, etc.
 *
 * The `ApolloClientLike` type is intentionally narrower than
 * `@apollo/client`'s full `ApolloClient` — it covers only the methods
 * this codebase actually calls (`.query`, `.mutate`, `.subscribe`,
 * `.watchQuery`). Tests can construct mocks against this shape without
 * standing up the full link + cache stack, and the structural typing
 * means the real `apolloClient` is assignable to it without casts in
 * either direction.
 */
import { hasInjectionContext } from 'vue'

import { apolloClient } from '@/shared/api/apolloClient'
import { useApollo } from '@/shared/ports/apollo'

/**
 * Structural subset of `ApolloClient` that the codebase's GraphQL
 * dispatch paths use. Add a method here ONLY if a slice genuinely needs
 * it — keeping the surface narrow is what makes the test-time mocks
 * small (one object literal with a couple of async functions).
 */
export interface ApolloClientLike {
  readonly query: <T>(options: {
    readonly query: unknown
    readonly variables?: Record<string, unknown>
    readonly fetchPolicy?: string
  }) => Promise<{ readonly data?: T | null | undefined }>
  readonly mutate: <T>(options: {
    readonly mutation: unknown
    readonly variables?: Record<string, unknown>
  }) => Promise<{ readonly data?: T | null | undefined }>
  readonly subscribe: (options: {
    readonly query: unknown
    readonly variables?: Record<string, unknown>
    readonly fetchPolicy?: string
  }) => { subscribe: (observer: {
    readonly next?: (event: { readonly data?: unknown }) => void
    readonly error?: (e: unknown) => void
  }) => { readonly unsubscribe: () => void } }
  readonly watchQuery: (options: {
    readonly query: unknown
    readonly variables?: Record<string, unknown>
  }) => unknown
}

/**
 * Resolve the Apollo client a slice will dispatch through.
 *
 * Priority:
 *   1. An explicitly injected client (test mocks, deterministic harnesses).
 *   2. `useApollo()` from the host port — available inside any Vue setup
 *      context (production path: a composable called from
 *      `gvgPlannerStore`'s setup-style body, which runs inside the
 *      `provideApolloClient` scope).
 *   3. The direct `apolloClient` singleton from
 *      `@/shared/api/apolloClient` — fallback for non-setup callers
 *      (module-scope `const apolloClient = resolveClient()` binds,
 *      `node --test` harnesses, the `runReporting` harness mock,
 *      any side-channel call). `useApollo()` throws outside an
 *      injection context, and we catch that here so every caller —
 *      setup or not, dev or production — gets the same host-owned
 *      client instance the rest of the app uses.
 */
export function resolveClient(injected?: ApolloClientLike): ApolloClientLike {
  if (injected !== undefined) return injected
  if (hasInjectionContext()) {
    try {
      return useApollo() as unknown as ApolloClientLike
    } catch {
      // Fall through to the singleton.
    }
  }
  return apolloClient as unknown as ApolloClientLike
}
